import {useEffect, useRef, type CSSProperties, type ReactNode} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import type {VisibleRange} from './data-grid';

export function VirtualList<T>({
  items,
  itemHeight,
  renderItem,
  onRangeChange,
  style,
}: {
  items: T[];
  itemHeight: number;
  renderItem: (item: T, options: {index: number; first: boolean; last: boolean}) => ReactNode;
  onRangeChange?: (range: VisibleRange) => void;
  style?: CSSProperties;
}) {
  const scroll = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scroll.current,
    estimateSize: () => itemHeight,
    overscan: 5,
    useAnimationFrameWithResizeObserver: true,
  });
  const visible = virtualizer.getVirtualItems();
  const start = visible[0]?.index;
  const end = visible.at(-1)?.index;
  const callback = useRef(onRangeChange);
  callback.current = onRangeChange;
  const pendingFocus = useRef<number | null>(null);
  useEffect(() => {
    if (pendingFocus.current == null) return;
    const target = scroll.current?.querySelector<HTMLElement>(`[data-index="${pendingFocus.current}"] [tabindex]`);
    if (target) {
      target.focus();
      pendingFocus.current = null;
    }
  }, [start, end, items]);
  useEffect(() => {
    if (start !== undefined && end !== undefined) callback.current?.({startIndex: start, endIndex: end + 1});
  }, [start, end, items.length]);
  return (
    <div
      ref={scroll}
      data-virtual-list
      className="overflow-auto"
      style={style}
      onKeyDown={(event) => {
        if (event.defaultPrevented || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const row = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-index]') : null;
        if (!row) return;
        const next = Number(row.dataset.index) + (event.key === 'ArrowDown' ? 1 : -1);
        if (next < 0 || next >= items.length) return;
        event.preventDefault();
        const target = scroll.current?.querySelector<HTMLElement>(`[data-index="${next}"] [tabindex]`);
        if (target) target.focus();
        else pendingFocus.current = next;
        virtualizer.scrollToIndex(next, {align: 'auto'});
      }}
    >
      <div style={{height: virtualizer.getTotalSize(), position: 'relative'}}>
        {visible.map((item) => (
          <div
            key={item.key}
            data-index={item.index}
            ref={virtualizer.measureElement}
            style={{position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${item.start}px)`}}
          >
            {renderItem(items[item.index], {
              index: item.index,
              first: item.index === 0,
              last: item.index === items.length - 1,
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export type {VisibleRange} from './data-grid';
